/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRequestContext } from '@msdyn365-commerce/core';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { IIncrementalQuantityProps } from '@msdyn365-commerce-modules/utilities/src/incremental-quantity/IncrementalQuantity.props';

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private _minValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                state.currentInput === 1 &&
                props.currentCount &&
                props.currentCount > IncrementalQuantity.defaultProps.min!)
        ) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1, isUpdating: false };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = this.props.min ?? IncrementalQuantity.defaultProps.min!;
    }

    public render(): JSX.Element {
        const { min, max } = this.props;

        this._minValue = min ?? IncrementalQuantity.defaultProps.min!;
        const value = this.state.currentInput;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = value <= this._minValue || this.props.isGiftCard;
        const incrementDisabled = value >= max || this.props.isGiftCard;
        const currentValue = value;
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${value}`;

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity'>
                    <button
                        disabled={this.props.disabled || decrementDisabled}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        {...decrementAttributes}
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        id={this.props.id}
                        type='number'
                        className='quantity-input'
                        pattern='[0-9]*'
                        value={value}
                        onChange={this._handleChange}
                        onBlur={this._validateMin}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={max}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        {...incrementAttributes}
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = currQuantity + 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity >= this._minValue) {
            invokeCallback = true;
            updatedQuantity = currQuantity - 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let currentValue = Number.parseInt(e.target.value, 10);
        if (Number.isNaN(currentValue)) {
            this.setState({ currentInput: currentValue });
            return;
        }
        currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
        this.setState({ currentInput: currentValue });
        debounce(() => {
            // Due of usage debouncer, we may ignore onChange return value
            this.props.onChange && this.props.onChange(this.state.currentInput);

            const inputElement =
                this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
            if (inputElement) {
                inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
                inputElement.setAttribute('value', this.state.currentInput.toString());
            }
        }, 1000)();
    }

    /**
     * Validates min and updates.
     */
    private readonly _validateMin = (): void => {
        if (Number.isNaN(this.state.currentInput)) {
            this.setState({ currentInput: this._minValue });
        } else {
            const currentValue = Math.min(this.props.max, Math.max(this._minValue, this.state.currentInput));
            if (currentValue !== this.state.currentInput) {
                this.setState({ currentInput: currentValue });
            }
        }
    };
}
